/**
 * Parser mínimo de WebVTT.
 *
 * Convierte el texto de un archivo `.vtt` en un array de cues con timing y texto.
 * Cubre el shape que produce AWS Transcribe con `Subtitles.Formats = ['vtt']`:
 */

export type Cue = {
  start: number;
  end: number;
  text: string;
};

/**
 * Convierte un timestamp WebVTT (`HH:MM:SS.mmm` o `MM:SS.mmm`) a segundos.
 * Formato tolerante — Transcribe siempre usa `HH:MM:SS.mmm` pero VTT admite ambos.
 */
const parseTimestamp = (ts: string): number => {
  const parts = ts.trim().split(':');
  if (parts.length === 3) {
    const [h, m, rest] = parts;
    return Number(h) * 3600 + Number(m) * 60 + Number(rest);
  }
  if (parts.length === 2) {
    const [m, rest] = parts;
    return Number(m) * 60 + Number(rest);
  }
  return NaN;
};

export const parseVTT = (vtt: string): Cue[] => {
  // Normalizar CRLF (por si viene de S3/Windows) y remover BOM inicial.
  const normalized = vtt.replace(/\r\n/g, '\n').replace(/^﻿/, '');

  // Sacar el header WEBVTT (con o sin metadata en la misma línea).
  const withoutHeader = normalized.replace(/^WEBVTT[^\n]*\n+/i, '');

  // Separar en bloques por doble salto de línea.
  const blocks = withoutHeader.split(/\n\n+/).filter(b => b.trim().length > 0);

  const cues: Cue[] = [];

  for (const block of blocks) {
    const lines = block.split('\n').filter(l => l.length > 0);
    // Buscar la línea con `-->` — puede estar en índice 0 (sin id) o 1 (con id).
    const timingIdx = lines.findIndex(l => l.includes('-->'));
    if (timingIdx < 0) continue;

    const timingLine = lines[timingIdx];
    // `HH:MM:SS.mmm --> HH:MM:SS.mmm [cue-settings]` — descartamos cue-settings.
    const [startStr, endWithSettings] = timingLine.split('-->');
    if (!startStr || !endWithSettings) continue;
    const endStr = endWithSettings.trim().split(/\s+/)[0];

    const start = parseTimestamp(startStr);
    const end = parseTimestamp(endStr);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;

    const text = lines
      .slice(timingIdx + 1)
      .join(' ')
      .trim();

    if (!text) continue;

    cues.push({ start, end, text });
  }

  return cues;
};
